import React from "react";
import { ClipLoader } from "react-spinners";

import { AUTOPILOT_COLORS } from "./styles";
import { getAutopilotHealthSummary } from "./healthSummary";

const GAUGE_SIZE = 78;
const GAUGE_STROKE = 8;
const GAUGE_RADIUS = (GAUGE_SIZE - GAUGE_STROKE) / 2;
const GAUGE_CIRCUMFERENCE = 2 * Math.PI * GAUGE_RADIUS;

function formatGaugeValue(taxa) {
  if (taxa === null || taxa === undefined) return "—";
  return Number.isInteger(taxa) ? `${taxa}%` : `${taxa.toFixed(1)}%`;
}

export default function HealthGauge({ data, loading, onClick }) {
  const { taxa, statusLabel, statusColor } = getAutopilotHealthSummary(data);
  const progress = taxa !== null ? Math.max(0, Math.min(100, taxa)) : 0;
  const dashOffset = GAUGE_CIRCUMFERENCE - (progress / 100) * GAUGE_CIRCUMFERENCE;
  const isLoading = loading && !data;

  return (
    <button
      type="button"
      onClick={onClick}
      className="everlog-card-interactive"
      style={{
        width: "100%",
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        gap: "0.85rem",
        padding: "0.82rem 0.88rem",
        borderRadius: "18px",
        border: `1px solid ${taxa !== null ? `color-mix(in srgb, ${statusColor} 33%, transparent)` : "var(--theme-accent-border)"}`,
        background:
          taxa !== null
            ? `radial-gradient(circle at top left, color-mix(in srgb, ${statusColor} 12%, transparent), transparent 52%), var(--everlog-card-surface-elevated)`
            : "var(--everlog-card-surface-elevated)",
        boxShadow:
          taxa !== null
            ? `var(--everlog-card-elevated-shadow), 0 0 0 1px color-mix(in srgb, ${statusColor} 7%, transparent) inset`
            : "var(--everlog-card-elevated-shadow)",
        color: AUTOPILOT_COLORS.title,
        textAlign: "left",
        cursor: "pointer",
      }}
      aria-label="Abrir monitor de integração"
    >
      <div style={{ position: "relative", width: GAUGE_SIZE, height: GAUGE_SIZE, flexShrink: 0 }}>
        <svg width={GAUGE_SIZE} height={GAUGE_SIZE} viewBox={`0 0 ${GAUGE_SIZE} ${GAUGE_SIZE}`}>
          <circle
            cx={GAUGE_SIZE / 2}
            cy={GAUGE_SIZE / 2}
            r={GAUGE_RADIUS}
            fill="none"
            stroke={AUTOPILOT_COLORS.gaugeTrack}
            strokeWidth={GAUGE_STROKE}
          />
          <circle
            cx={GAUGE_SIZE / 2}
            cy={GAUGE_SIZE / 2}
            r={GAUGE_RADIUS}
            fill="none"
            stroke={taxa !== null ? statusColor : AUTOPILOT_COLORS.gaugeDefault}
            strokeWidth={GAUGE_STROKE}
            strokeLinecap="round"
            strokeDasharray={GAUGE_CIRCUMFERENCE}
            strokeDashoffset={dashOffset}
            transform={`rotate(-90 ${GAUGE_SIZE / 2} ${GAUGE_SIZE / 2})`}
          />
        </svg>

        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: "0.12rem",
          }}
        >
          {isLoading ? (
            <ClipLoader size={20} color="var(--theme-accent)" />
          ) : (
            <>
              <span style={{ color: AUTOPILOT_COLORS.title, fontSize: "0.98rem", fontWeight: 700, lineHeight: 1 }}>
                {formatGaugeValue(taxa)}
              </span>
              <span style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.6rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                saúde
              </span>
            </>
          )}
        </div>
      </div>

      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "0.22rem" }}>
          Sustentação
        </div>
        <div style={{ color: statusColor, fontSize: "0.94rem", fontWeight: 700, lineHeight: 1.18 }}>
          {isLoading ? "Analisando sustentação..." : statusLabel}
        </div>
      </div>

      <span style={{ color: taxa !== null ? statusColor : "var(--theme-accent)", fontSize: "0.94rem", flexShrink: 0 }}>↗</span>
    </button>
  );
}
